// Unavailability API for Substitution Management
// Uses direct-fetch pattern with overlap detection and date-range support

/// <reference lib="dom" />

import { handleApiCall, ValidationError, ConflictError } from './errors'
import { getAccessToken } from './auth-token'
import type {
  UnavailabilityWithRelations,
  CreateUnavailabilityRequest,
  GetUnavailabilityParams,
} from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

// Helper: fetch a single unavailability record with teacher join
async function fetchUnavailabilityById(
  id: string,
  accessToken: string
): Promise<UnavailabilityWithRelations> {
  const params = new URLSearchParams()
  params.append('select', '*,teacher:teachers(*)')
  params.append('id', `eq.${id}`)

  const response = await fetch(`${SUPABASE_URL}/rest/v1/unavailability?${params}`, {
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/vnd.pgrst.object+json',
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to fetch unavailability record')
  }

  return response.json()
}

/**
 * Create a new unavailability record.
 * Validates that end_date >= start_date and checks for overlapping records.
 */
export async function createUnavailability(
  req: CreateUnavailabilityRequest
): Promise<UnavailabilityWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // 1. Validate date range
    if (req.end_date < req.start_date) {
      throw new ValidationError('end_date must be on or after start_date', {
        end_date: 'Must be on or after start_date',
      })
    }

    // 2. Check for overlapping unavailability records
    const overlapParams = new URLSearchParams()
    overlapParams.append('select', 'id,start_date,end_date')
    overlapParams.append('teacher_id', `eq.${req.teacher_id}`)
    overlapParams.append('start_date', `lte.${req.end_date}`)
    overlapParams.append('end_date', `gte.${req.start_date}`)

    const overlapResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/unavailability?${overlapParams}`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    if (!overlapResponse.ok) {
      const error = await overlapResponse.json()
      throw new Error(error.message || 'Failed to check for overlapping unavailability')
    }

    const overlapping: { id: string; start_date: string; end_date: string }[] =
      await overlapResponse.json()

    if (overlapping.length > 0) {
      throw new ConflictError(
        'Teacher already has an unavailability record overlapping this date range',
        overlapping
      )
    }

    // 3. Insert the new record (include legacy `date` field = start_date)
    const insertResponse = await fetch(`${SUPABASE_URL}/rest/v1/unavailability`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify({
        school_id: req.school_id,
        teacher_id: req.teacher_id,
        date: req.start_date, // legacy column
        start_date: req.start_date,
        end_date: req.end_date,
        reason: req.reason ?? null,
        created_by: req.created_by ?? null,
      }),
    })

    if (!insertResponse.ok) {
      const error = await insertResponse.json()
      throw new Error(error.message || 'Failed to create unavailability')
    }

    const data: any[] = await insertResponse.json()
    const created = data[0]

    // 4. Fetch with teacher join and return
    return fetchUnavailabilityById(created.id, accessToken)
  })
}

/**
 * Query unavailability records with optional filters.
 * Results are ordered by start_date descending.
 */
export async function getUnavailability(
  params: GetUnavailabilityParams
): Promise<UnavailabilityWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const queryParams = new URLSearchParams()
    queryParams.append('select', '*,teacher:teachers(*)')
    queryParams.append('school_id', `eq.${params.school_id}`)

    if (params.teacher_id) {
      queryParams.append('teacher_id', `eq.${params.teacher_id}`)
    }
    if (params.from_date) {
      queryParams.append('start_date', `gte.${params.from_date}`)
    }
    if (params.to_date) {
      queryParams.append('end_date', `lte.${params.to_date}`)
    }

    queryParams.append('order', 'start_date.desc')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/unavailability?${queryParams}`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch unavailability records')
    }

    return response.json()
  })
}

/**
 * Delete an unavailability record by ID.
 */
export async function deleteUnavailability(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/unavailability?id=eq.${id}`,
      {
        method: 'DELETE',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete unavailability')
    }
  })
}
